/** Equipment tags may be nested at most this many levels (e.g. Audio › Mikrofon › Zubehör). */
export const MAX_EQUIPMENT_TAG_DEPTH = 3;
function parentMap(tags) {
    return new Map(tags.map((tag) => [tag.id, tag.parentID ?? null]));
}
/** 1-based level of a tag: top-level tags are level 1. */
export function equipmentTagLevel(tags, tagId) {
    const parents = parentMap(tags);
    const visited = new Set();
    let level = 1;
    let parentId = parents.get(tagId) ?? null;
    while (parentId && !visited.has(parentId)) {
        visited.add(parentId);
        level += 1;
        parentId = parents.get(parentId) ?? null;
    }
    return level;
}
/** Levels spanned by a tag and its sub-tags: 1 for a tag without sub-tags. */
export function equipmentTagSubtreeHeight(tags, tagId) {
    const children = new Map();
    for (const tag of tags) {
        if (!tag.parentID)
            continue;
        const siblings = children.get(tag.parentID) ?? [];
        siblings.push(tag.id);
        children.set(tag.parentID, siblings);
    }
    const visited = new Set();
    const height = (id) => {
        if (visited.has(id))
            return 0;
        visited.add(id);
        return 1 + Math.max(0, ...(children.get(id) ?? []).map(height));
    };
    return height(tagId);
}
/**
 * Checks whether `tagId` (or a new tag when null) may be placed under
 * `parentId`. Returns an error message, or null when the placement is valid.
 */
export function validateEquipmentTagParent(tags, tagId, parentId) {
    if (!parentId)
        return null;
    if (!tags.some((tag) => tag.id === parentId)) {
        return "Parent tag not found";
    }
    if (tagId) {
        // The new parent must not be the tag itself or one of its sub-tags
        const parents = parentMap(tags);
        const visited = new Set();
        let current = parentId;
        while (current && !visited.has(current)) {
            if (current === tagId) {
                return "A tag cannot be moved into itself or one of its sub-tags";
            }
            visited.add(current);
            current = parents.get(current) ?? null;
        }
    }
    const height = tagId ? equipmentTagSubtreeHeight(tags, tagId) : 1;
    if (equipmentTagLevel(tags, parentId) + height > MAX_EQUIPMENT_TAG_DEPTH) {
        return `Tags can be nested at most ${MAX_EQUIPMENT_TAG_DEPTH} levels deep`;
    }
    return null;
}
